import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Label,
  Loader,
  Message,
} from "semantic-ui-react";
import { fetchFeedRuns, latestRunPerSupplier } from "./feedRunsApi";
import { SUPPLIERS_BY_KEY } from "../../suppliers";
import { renderDate } from "../../../../../formatting/dateFormat";

const STATUS_COLORS = { succeeded: "green", failed: "red", running: "blue" };

const supplierName = (key) =>
  key === "*" ? "DTB ranking" : SUPPLIERS_BY_KEY[key]?.name || key;

const RunRow = ({ run }) => (
  <Table.Row negative={run.status === "failed"}>
    <Table.Cell>{supplierName(run.supplier_key)}</Table.Cell>
    <Table.Cell>{run.source}</Table.Cell>
    <Table.Cell>
      <Label size="tiny" color={STATUS_COLORS[run.status] || "grey"}>
        {run.status}
      </Label>
    </Table.Cell>
    <Table.Cell>{renderDate(run.started_at)}</Table.Cell>
    <Table.Cell>
      {run.finished_at ? renderDate(run.finished_at) : ""}
    </Table.Cell>
    <Table.Cell textAlign="right">{run.rows_in_file ?? ""}</Table.Cell>
    <Table.Cell textAlign="right">{run.rows_matched ?? ""}</Table.Cell>
    <Table.Cell>{run.triggered_by || ""}</Table.Cell>
    <Table.Cell title={run.message || ""}>
      {run.file_name ? <div>{run.file_name}</div> : null}
      <div style={{ color: "rgba(0,0,0,0.6)" }}>{run.message}</div>
    </Table.Cell>
  </Table.Row>
);

const RunsTable = ({ runs }) => (
  <Table compact size="small" celled>
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>Supplier</Table.HeaderCell>
        <Table.HeaderCell>Source</Table.HeaderCell>
        <Table.HeaderCell>Status</Table.HeaderCell>
        <Table.HeaderCell>Started</Table.HeaderCell>
        <Table.HeaderCell>Finished</Table.HeaderCell>
        <Table.HeaderCell>Rows</Table.HeaderCell>
        <Table.HeaderCell>Matched</Table.HeaderCell>
        <Table.HeaderCell>By</Table.HeaderCell>
        <Table.HeaderCell>File / message</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {runs.map((run) => (
        <RunRow key={run.id} run={run} />
      ))}
    </Table.Body>
  </Table>
);

/**
 * Supplier Feeds: when each supplier's data last refreshed and whether it
 * worked (FTP feeds, CSV uploads, clears and the DTB ranking).
 */
const SupplierFeedsModal = ({ closeModal }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRuns(await fetchFeedRuns({ pageSize: 200 }));
    } catch (err) {
      setError(`Could not load supplier feed runs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const latest = latestRunPerSupplier(runs);
  const recent = runs.slice(0, 25);

  return (
    <Modal open onClose={closeModal} size="large">
      <Modal.Header>Supplier Feeds</Modal.Header>
      <Modal.Content scrolling>
        {error && <Message negative>{error}</Message>}
        {loading ? (
          <Loader active inline="centered" />
        ) : (
          <>
            <h4>Latest run per supplier</h4>
            {latest.length === 0 ? (
              <p>No feed runs recorded yet.</p>
            ) : (
              <RunsTable runs={latest} />
            )}
            <h4>Recent runs</h4>
            <RunsTable runs={recent} />
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={load} icon="refresh" content="Refresh" />
        <Button positive onClick={closeModal} content="Close" />
      </Modal.Actions>
    </Modal>
  );
};

export default SupplierFeedsModal;
